import { api } from '@verifime/api-definition';
import type { RecoveryReason } from '@verifime/cognito-passwordless-auth';
import { SERVER_ERRORS } from '@verifime/utils';
import { FormEvent, useEffect, useRef, useState } from 'react';

const HTTP_CONFLICT = 409;

/** The action endpoint answers 409 for a completed tracking reference (person-api#2044), with no
 *  body, so the status is the whole signal. A 4xx reaches the caller as a raw AxiosError carrying
 *  `response.status`; the api client only rewrites 5xx (see interceptorWithToken.ts). Structural,
 *  so this view takes no axios dependency. */
function isConflict(error: unknown): boolean {
  const status = (error as { response?: { status?: unknown } } | undefined)?.response?.status;
  return status === HTTP_CONFLICT;
}

export type TUseRecoverInvitationParams = {
  trackId: string;
  initialEmail: string;
  onLoginRedirect: (email: string) => void;
};

export type TUseRecoverInvitation = {
  email: string;
  isSubmitting: boolean;
  errorMessage: string | undefined;
  /** Set when the action endpoint answers 409. It stands in for the generic error and is neutral
   *  on purpose (#571 Q9): the recovery page has no sign-in, so it must not say why the customer
   *  has to sign in. The page passes it to RecoverInvitation as its `reason`. */
  recoveryReason: RecoveryReason | undefined;
  sentToEmail: string | undefined;
  onEmailChange: (email: string) => void;
  onSubmit: (e: FormEvent) => void;
  /** Re-runs the same action-endpoint call as onSubmit, for the LinkSentResend affordance
   *  rendered on the sent screen. Takes no event (there is no form submission to prevent-default
   *  on a press of the resend control) and reuses `email`, the value onSubmit already sent. */
  onResend: () => void;
  /** The moment the last successful send happened: set once, alongside `sentToEmail` itself, and
   *  again on a genuine resend, but only when that resend's outcome actually sends (not a LOGIN
   *  redirect, not an error) - see `submit`'s own comment. This hook is the layer that actually
   *  fires the send (via the action-endpoint call), so it is the only layer that genuinely knows
   *  when that was; the page derives LinkSent's `expiresAt` from this via computeLinkExpiry
   *  rather than computing its own, matching GoogleSsoSignUpView's identical `sentAt` (see that
   *  hook's own doc comment). Null before any send. */
  sentAt: Date | null;
};

export function useRecoverInvitation({
  trackId,
  initialEmail,
  onLoginRedirect,
}: TUseRecoverInvitationParams): TUseRecoverInvitation {
  const [email, setEmail] = useState(initialEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [recoveryReason, setRecoveryReason] = useState<RecoveryReason | undefined>(undefined);
  const [sentToEmail, setSentToEmail] = useState<string | undefined>(undefined);
  const [sentAt, setSentAt] = useState<Date | null>(null);
  const touched = useRef(false);

  useEffect(() => {
    if (initialEmail && !touched.current) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  const onEmailChange = (value: string) => {
    touched.current = true;
    setEmail(value);
  };

  const submit = () => {
    setErrorMessage(undefined);
    setIsSubmitting(true);
    api
      .postV1usersignuptrackingTrackingReferenceaction(
        { email },
        { params: { trackingReference: trackId } },
      )
      .then((response) => {
        if (response.action === 'LOGIN') {
          onLoginRedirect(email);
          return;
        }
        setSentToEmail(email);
        // This function serves both the first submit and every resend (onResend below just
        // calls it again), and a fresh link was genuinely minted whenever this line runs, so
        // sentAt advances here every time - never on an outcome that redirected or sent nothing.
        setSentAt(new Date());
      })
      .catch((error: unknown) => {
        if (isConflict(error)) {
          // A resend from the sent screen can answer 409 too: the sent state ends here, so the
          // page stops drawing the "Email sent!" screen over the reason.
          setSentToEmail(undefined);
          setRecoveryReason('sign-in-required');
          return;
        }
        setErrorMessage(SERVER_ERRORS.default);
      })
      .finally(() => setIsSubmitting(false));
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };

  const onResend = () => {
    submit();
  };

  return {
    email,
    isSubmitting,
    errorMessage,
    recoveryReason,
    sentToEmail,
    onEmailChange,
    onSubmit,
    onResend,
    sentAt,
  };
}
